import React, { useState, useEffect } from 'react';
import DiagnosticHeader from '../components/Diagnostic/DiagnosticHeader';
import Footer from '../components/Footer';

/**
 * Diagnostic Selector Page
 * Entry point for requesting a diagnostic assessment
 */
function DiagnosticSelector() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    gdprConsent: false,
    diagnosticType: ''
  });

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [errors, setErrors] = useState({});
  const [submitMessage, setSubmitMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateEmail = (email) => {
    return emailRegex.test(email);
  };

  const handleEmailBlur = () => {
    if (formData.email && !validateEmail(formData.email)) {
      setErrors(prev => ({ ...prev, email: 'Please enter a valid email address' }));
    } else {
      setErrors(prev => ({ ...prev, email: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Check all required fields
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.gdprConsent) {
      newErrors.gdprConsent = 'You must consent to GDPR terms';
    }
    if (!formData.diagnosticType) {
      newErrors.diagnosticType = 'Please select a diagnostic type';
    }

    // Show general error if any field is empty
    if (Object.keys(newErrors).length > 0) {
      newErrors.general = 'Please fill in all required fields';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitMessage('');

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('/api/diagnostic-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          requestingPageId: 1  // Standard diagnostic request form
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitMessage(data.message || 'Diagnostic request sent! Check your email for the link.');
        // Clear form on success
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          gdprConsent: false,
          diagnosticType: ''
        });
      } else {
        setErrors({ general: data.error || 'Failed to submit request. Please try again.' });
      }
    } catch (error) {
      setErrors({ general: 'Network error. Please check your connection and try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <DiagnosticHeader showNav={false} />

      <div className="pt-32 pb-20 px-4 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-2xl w-full">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Request Your Diagnostic
        </h1>
        <p className="text-gray-600 mb-8">
          Complete this form to receive your personalized diagnostic link via email.
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* First Name */}
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
              First Name *
            </label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              aria-label="First Name"
            />
            {errors.firstName && (
              <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>
            )}
          </div>

          {/* Last Name */}
          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
              Last Name *
            </label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              aria-label="Last Name"
            />
            {errors.lastName && (
              <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              Email Address *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              onBlur={handleEmailBlur}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              aria-label="Email"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email}</p>
            )}
          </div>

          {/* Diagnostic Type Selection */}
          <div>
            <fieldset>
              <legend className="block text-sm font-medium text-gray-700 mb-3">
                Select Diagnostic Type *
              </legend>
              <div className="space-y-3">
                <div className="bg-[#EAEFF5] p-6 rounded-lg border-l-4 border-[#062B49] flex items-start cursor-pointer hover:bg-[#DCE5ED] transition-colors">
                  <input
                    type="radio"
                    id="systemBurden"
                    name="diagnosticType"
                    value="system_burden"
                    checked={formData.diagnosticType === 'system_burden'}
                    onChange={handleInputChange}
                    className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="systemBurden" className="ml-3 cursor-pointer flex-1">
                    <span className="block text-sm font-medium text-[#062B49]">System Burden Diagnostic</span>
                    <span className="block text-sm text-[#697A90]">
                      Assess organizational dependencies and hidden work impact
                    </span>
                  </label>
                </div>

                <div className="bg-[#EAEFF5] p-6 rounded-lg border-l-4 border-[#062B49] flex items-start cursor-pointer hover:bg-[#DCE5ED] transition-colors">
                  <input
                    type="radio"
                    id="leadershipBurden"
                    name="diagnosticType"
                    value="leadership_burden"
                    checked={formData.diagnosticType === 'leadership_burden'}
                    onChange={handleInputChange}
                    className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="leadershipBurden" className="ml-3 cursor-pointer flex-1">
                    <span className="block text-sm font-medium text-[#062B49]">Leadership Burden Diagnostic</span>
                    <span className="block text-sm text-[#697A90]">
                      Measure leadership anxiety and transformation pressure
                    </span>
                  </label>
                </div>

                <div className="bg-[#EAEFF5] p-6 rounded-lg border-l-4 border-[#062B49] flex items-start cursor-pointer hover:bg-[#DCE5ED] transition-colors">
                  <input
                    type="radio"
                    id="psychologicalFlexibility"
                    name="diagnosticType"
                    value="psychological_flexibility"
                    checked={formData.diagnosticType === 'psychological_flexibility'}
                    onChange={handleInputChange}
                    className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="psychologicalFlexibility" className="ml-3 cursor-pointer flex-1">
                    <span className="block text-sm font-medium text-[#062B49]">Psychological Flexibility Diagnostic</span>
                    <span className="block text-sm text-[#697A90]">
                      Assess adaptive capacity and psychological resilience
                    </span>
                  </label>
                </div>
              </div>
            </fieldset>
            {errors.diagnosticType && (
              <p className="mt-2 text-sm text-red-600">{errors.diagnosticType}</p>
            )}
          </div>

          {/* GDPR Consent */}
          <div>
            <div className="bg-[#EAEFF5] p-6 rounded-lg border border-gray-300">
              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="gdprConsent"
                  name="gdprConsent"
                  checked={formData.gdprConsent}
                  onChange={handleInputChange}
                  className="mt-1 h-4 w-4 text-[#062B49] focus:ring-[#86C2CA] border-gray-400 rounded"
                />
                <label htmlFor="gdprConsent" className="ml-3 text-sm text-[#062B49] cursor-pointer">
                  I consent to Daring Futures storing my information and sending me diagnostic results via email. *
                </label>
              </div>
            </div>
            {errors.gdprConsent && (
              <p className="mt-2 text-sm text-red-600">{errors.gdprConsent}</p>
            )}
          </div>

          {/* General Errors */}
          {errors.general && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-sm text-red-800">{errors.general}</p>
            </div>
          )}

          {/* Success Message */}
          {submitMessage && (
            <div className="bg-green-50 border border-green-200 rounded-md p-4">
              <p className="text-sm text-green-800">{submitMessage}</p>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-[#86C2CA] hover:bg-[#3B4F6B] text-white font-poppins font-bold px-6 py-2.5 rounded transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Sending Request...' : 'Request Diagnostic'}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-gray-500">
          * Required fields
        </p>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default DiagnosticSelector;
